"use client"

import { useMemo, useState, type ReactNode } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"

const FALLBACK_RELATED = [
  "ice-maker-size-calculator",
  "ice-maker-capacity-calculator",
  "energy-cost-calculator",
  "refrigerator-size-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "Ice Maker Energy Cost Calculator",
  category: "Energy & Costs" as const,
  mediumDescription:
    "Estimate yearly electricity cost for built-in, standalone, or commercial ice makers. Uses ENERGY STAR-style kWh-per-pound rates and the US average residential electricity price.",
  faqs: [
    {
      question: "How much electricity does a typical ice maker use?",
      answer:
        "Built-in fridge ice makers use about 0.12 kWh per pound of ice. Standalone countertop units use ~0.15 kWh/lb, and commercial ice machines run ~0.10 kWh/lb thanks to scale efficiency.",
    },
    {
      question: "Why does nugget or clear ice cost more to make?",
      answer:
        "Nugget ice involves extruding crushed ice — about 15% more energy. Clear ice runs multiple slow freeze cycles to push out dissolved gases and is roughly 25% more energy-intensive than standard cubes.",
    },
    {
      question: "Are ice makers ENERGY STAR rated?",
      answer:
        "Standalone commercial automatic ice makers can earn ENERGY STAR certification — listed models use roughly 15% less energy than baseline. Residential built-in ice makers in fridges are covered under the refrigerator's ENERGY STAR rating.",
    },
    {
      question: "What electricity rate does this use?",
      answer:
        "We use $0.13/kWh, near the EIA US residential average. Adjust the annual cost up or down using your local rate ÷ 0.13.",
    },
    {
      question: "Is making ice cheaper than buying bagged ice?",
      answer:
        "Bagged ice averages roughly $0.30 per pound retail. At about 1.3 to 2 cents per pound to produce at home, even premium clear ice is about 10–20× cheaper to make than to buy.",
    },
  ],
  relatedTools: ["ice-maker-size-calculator", "ice-maker-capacity-calculator", "energy-cost-calculator"],
}

export function IceMakerEnergyCostPageClient({ children }: { children?: ReactNode }) {
  const [iceMakerType, setIceMakerType] = useState("built-in")
  const [dailyProduction, setDailyProduction] = useState("50")
  const [iceType, setIceType] = useState("cube")

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    const production = parseFloat(dailyProduction)
    if (!production || production <= 0) return null

    let kwhPerPound = 0
    if (iceMakerType === "built-in") kwhPerPound = 0.12
    else if (iceMakerType === "standalone") kwhPerPound = 0.15
    else kwhPerPound = 0.1

    if (iceType === "nugget") kwhPerPound *= 1.15
    else if (iceType === "clear") kwhPerPound *= 1.25

    const kwhPerDay = production * kwhPerPound
    const kwhPerYear = kwhPerDay * 365
    const electricityRate = 0.13
    const dailyCost = kwhPerDay * electricityRate
    const monthlyCost = dailyCost * 30
    const annualCost = kwhPerYear * electricityRate
    const costPerPound = kwhPerPound * electricityRate

    const storeBoughtCost = production * 365 * 0.03
    const savings = storeBoughtCost - annualCost

    return {
      kwhPerDay: parseFloat(kwhPerDay.toFixed(2)),
      kwhPerYear: Math.round(kwhPerYear),
      dailyCost: parseFloat(dailyCost.toFixed(2)),
      monthlyCost: parseFloat(monthlyCost.toFixed(2)),
      annualCost: parseFloat(annualCost.toFixed(2)),
      costPerPound: parseFloat(costPerPound.toFixed(3)),
      storeBoughtCost: parseFloat(storeBoughtCost.toFixed(2)),
      savings: parseFloat(savings.toFixed(2)),
    }
  }, [iceMakerType, dailyProduction, iceType])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="What does ice"
      heroTitleItalic="actually"
      heroTitlePost="cost per year?"
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~15 sec", "EIA residential rate · 365-day model"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "0.12", label: "kWh / lb built-in", note: "Average" },
            { value: "+25%", label: "Clear ice premium", note: "Energy" },
            { value: "$0.13", label: "Per kWh", note: "EIA average" },
            { value: "$0.30", label: "Bagged ice retail", note: "Per lb" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "ice-maker-energy-cost-calculator")}
      cta={{
        title: "More ice. Lower bill. Right machine.",
        body: "Browse undercounter and countertop ice makers sized to fit kitchens, bars, and outdoor spaces.",
        buttonLabel: "Browse ice makers",
        buttonHref: "/ice-makers",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Type, output,
                <br />
                ice form.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Commercial machines are most efficient per pound; clear ice is the most expensive to produce.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label htmlFor="ice-maker-energy-type" className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Ice maker type
                  </label>
                  <select
                    id="ice-maker-energy-type"
                    value={iceMakerType}
                    onChange={(e) => setIceMakerType(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="built-in">Built-in (0.12 kWh/lb)</option>
                    <option value="standalone">Standalone / countertop (0.15 kWh/lb)</option>
                    <option value="commercial">Commercial (0.10 kWh/lb)</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="ice-maker-daily-production" className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Daily production
                  </label>
                  <div className="relative">
                    <input
                      id="ice-maker-daily-production"
                      type="number"
                      value={dailyProduction}
                      onChange={(e) => setDailyProduction(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">lbs/day</span>
                  </div>
                </div>
                <div>
                  <label htmlFor="ice-maker-ice-type" className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Ice type
                  </label>
                  <select
                    id="ice-maker-ice-type"
                    value={iceType}
                    onChange={(e) => setIceType(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="cube">Cube (baseline)</option>
                    <option value="nugget">Nugget (+15%)</option>
                    <option value="clear">Clear (+25%)</option>
                  </select>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Clear ice runs multiple slow freeze cycles.
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }}
                />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Annual electricity cost
                  </div>
                  <div
                    className="font-black"
                    style={{ fontSize: "clamp(36px, 8vw, 88px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}
                  >
                    {result ? `$${result.annualCost.toFixed(2)}` : "—"}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">/yr</span>
                  </div>
                  <div className="mt-4 text-[15px] text-white/85 leading-[1.5]">
                    {result
                      ? `vs. $${result.storeBoughtCost.toFixed(2)} buying bagged ice — saves $${Math.max(result.savings, 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/yr`
                      : "Enter daily production to begin."}
                  </div>
                  <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Cost per lb</div>
                      <div className="text-[16px] sm:text-[20px] font-black tracking-tight">{result ? `${(result.costPerPound * 100).toFixed(1)}¢` : "—"}</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Monthly</div>
                      <div className="text-[16px] sm:text-[20px] font-black tracking-tight">{result ? `$${result.monthlyCost.toFixed(2)}` : "—"}</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">kWh / yr</div>
                      <div className="text-[16px] sm:text-[20px] font-black tracking-tight">{result?.kwhPerYear ?? "—"}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Built-in is cheapest"
          title="French door fridges with built-in ice"
          titleMatch={["ice maker", "icemaker"]}
          description="The lowest cost-per-pound configuration for households — fridge ice makers run ~0.12 kWh/lb."
          collectionType="french-door"
          fallbackCollectionType="top-freezer"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                EIA average. <em className="italic text-[#ff9951]">Real</em> kWh per pound.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                kWh-per-pound rates approximate ENERGY STAR baseline values for residential and commercial ice makers. Cost uses the EIA US residential average of $0.13/kWh.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Energy per pound",
                  d: "Built-in 0.12 kWh/lb · standalone 0.15 · commercial 0.10. Nugget +15%, clear +25%.",
                },
                {
                  n: "02",
                  t: "Annual kWh",
                  d: "Daily lbs × kWh/lb × 365 days. Multiply by $0.13 to get annual cost.",
                },
                {
                  n: "03",
                  t: "Bagged-ice comparison",
                  d: "Bagged ice averages $0.30/lb retail. We multiply your daily lbs × 365 × $0.03 wholesale baseline to compare.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Top-freezer fridges with low yearly kWh"
          description="The most energy-efficient form factor in the fridge world — pair with a separate ice maker only if you need volume."
          collectionType="top-freezer"
          fallbackCollectionType="bottom-freezer"
        />
      </section>
      {/* Server-rendered supporting data belongs inside the shell, before its footer. */}
      {children}
    </ToolPageShell>
  )
}
